<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useTranslations } from '@/composables/useTranslations';

const { t } = useTranslations();

// --- LOGIQUE DE RÉCUPÉRATION DES IMAGES ---
let heroImages: { src: string; alt: string; overlayClass: string }[] = [];

try {
    const modules = import.meta.glob('/public/images/hero_*.{jpg,jpeg,png,webp}', {
        eager: true,
        import: 'default',
    });

    heroImages = Object.entries(modules)
        .filter(([path]) => path.includes('hero_'))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([path, module]) => {
            const fileName = path.split('/').pop() || '';
            const altKey = t(`home.hero.${fileName.match(/hero_(.+)\.[^/.]+$/)?.[1] || ''}`);

            return {
                src: module as string,
                alt: altKey,
                overlayClass: 'bg-gradient-to-r from-transparent to-black/20',
            };
        });
} catch (e) {
    console.error('Failed to load hero images', e);
}

// --- LOGIQUE DU CAROUSEL ---
const heroCarousel = ref<HTMLElement | null>(null);
const activeIndex = ref(0);
let heroInterval: ReturnType<typeof setInterval> | null = null;

// Met à jour l'index actif lors du défilement (manuel ou auto)
function handleScroll() {
    const carousel = heroCarousel.value;
    if (!carousel) return;

    // On calcule l'index en divisant la position actuelle par la largeur d'une slide
    const index = Math.round(carousel.scrollLeft / carousel.clientWidth);
    if (activeIndex.value !== index) {
        activeIndex.value = index;
    }
}

// Naviguer vers une slide spécifique
function goToSlide(index: number) {
    const carousel = heroCarousel.value;
    if (!carousel) return;

    carousel.scrollTo({
        left: index * carousel.clientWidth,
        behavior: 'smooth',
    });
    activeIndex.value = index;
}

// Passer à la slide suivante (utilisé par l'intervalle)
function slideHeroNext() {
    const carousel = heroCarousel.value;
    if (!carousel) return;

    const isLastSlide = activeIndex.value >= heroImages.length - 1;
    const nextIndex = isLastSlide ? 0 : activeIndex.value + 1;

    goToSlide(nextIndex);
}

// Gestion du cycle de vie
onMounted(() => {
    // On lance l'auto-scroll toutes les 5 secondes
    heroInterval = setInterval(slideHeroNext, 5000);
});

onBeforeUnmount(() => {
    if (heroInterval) clearInterval(heroInterval);
});
</script>

<template>
    <section class="relative w-full overflow-hidden px-4 py-4 md:px-6">
        <div class="group relative mx-auto max-w-[1800px]">
            <div
                ref="heroCarousel"
                @scroll="handleScroll"
                class="/* 2. Augmentation des hauteurs maximales */ no-scrollbar mx-auto flex aspect-[16/8.5] max-h-[600px] w-full snap-x snap-mandatory overflow-x-auto scroll-smooth rounded-2xl md:aspect-[16/7.5] md:max-h-[750px] lg:aspect-[16/6.5] lg:max-h-[900px]"
                :aria-label="t('home.heroCarouselLabel')"
            >
                <div v-for="(image, index) in heroImages" :key="image.src" class="relative h-full min-w-full snap-start overflow-hidden">
                    <img :src="image.src" :alt="image.alt" class="h-full w-full object-cover" :loading="index === 0 ? 'eager' : 'lazy'" />
                    <div class="absolute inset-0" :class="image.overlayClass"></div>
                </div>
            </div>
            <div v-if="heroImages.length > 1" class="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 gap-2.5">
                <button
                    v-for="(_, index) in heroImages"
                    :key="index"
                    @click="goToSlide(index)"
                    :class="[
                        'h-2 rounded-full transition-all duration-300 ease-in-out',
                        activeIndex === index ? 'w-8 bg-white shadow-sm' : 'w-2 bg-white/40 hover:bg-white/70',
                    ]"
                    :aria-label="t('home.heroSlideButton', { index: index + 1 })"
                ></button>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* Masquer la barre de défilement sur tous les navigateurs */
.no-scrollbar::-webkit-scrollbar {
    display: none;
}
.no-scrollbar {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
}
</style>
